import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'

const definitions = {
  global: {
    label: 'Niveau de risque global',
    definition: 'Synthèse de l’analyse de basculement, de lacet et de roulis fournie par le moteur pour ce chargement.',
    reading: 'Les niveaux faible, modéré, élevé et critique indiquent une vigilance croissante. « Inconnu » signifie que le niveau n’a pas été fourni.',
  },
  local: {
    label: 'Stabilité locale des piles',
    definition: 'Signale des transmissions de poids excentrées dans une pile ou au contact du plancher, ainsi que des équilibres locaux non vérifiés.',
    reading: 'Le nombre de cas compte les avertissements, pas nécessairement des piles distinctes. Un centre de gravité global bien placé ne suffit pas à valider chaque empilement.',
  },
  rollover: {
    label: 'Rollover Index (Basculement)',
    definition: 'Rapport entre la hauteur du centre de gravité utilisée par le modèle et la voie du véhicule (écartement transversal des roues). Il décrit la sensibilité géométrique au basculement.',
    reading: 'Sans unité : plus l’indice est bas, plus la géométrie est favorable. Repères affichés : faible ≤ 0,50 ; modéré ≤ 0,70 ; élevé ≤ 0,80 ; critique au-delà. Ce n’est pas une probabilité d’accident.',
  },
  yaw: {
    label: 'Facteur de Lacet (Yaw)',
    definition: 'Le lacet est la rotation autour de l’axe vertical, qui modifie la direction du véhicule. Cet indice combine le décentrage latéral du chargement et l’accélération latérale du scénario calculé.',
    reading: 'Plus le pourcentage est bas, plus l’indicateur est favorable. Repères affichés : faible ≤ 30 % ; modéré ≤ 50 % ; élevé ≤ 70 % ; critique au-delà. Le pourcentage exprime un indice, pas une probabilité d’accident.',
  },
  roll: {
    label: 'Roulis (Roll Ratio)',
    definition: 'Le roulis est l’inclinaison latérale autour de l’axe longitudinal du véhicule. Ce ratio compare l’effet de l’accélération latérale qui tend à faire basculer le chargement à l’effet stabilisateur lié à la voie du véhicule.',
    reading: 'Sans unité : plus le ratio est bas, plus l’indicateur est favorable. Repères affichés : faible ≤ 0,40 ; modéré ≤ 0,60 ; élevé ≤ 0,80 ; critique au-delà. Ce ratio n’est pas un angle ; sans accélération latérale dans le scénario, il peut être nul.',
  },
} as const

export default function StabilityIndicatorHelp({ indicator, isDark }: {
  indicator: keyof typeof definitions
  isDark: boolean
}) {
  const help = definitions[indicator]

  return (
    <details className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
      <summary
        title={`Afficher la définition : ${help.label}`}
        aria-label={`Définition : ${help.label}`}
        className={`w-fit inline-flex items-center justify-center p-0.5 cursor-pointer rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isDark
            ? 'text-slate-400 hover:text-blue-400 hover:bg-blue-500/10'
            : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'
          }`}
      >
        <QuestionMarkCircleIcon className="w-4 h-4" aria-hidden="true" />
      </summary>
      <div className={`mt-1.5 space-y-1.5 border-l-2 pl-2.5 py-1 text-[11px] leading-relaxed ${isDark ? 'border-blue-400/40 bg-blue-500/5' : 'border-blue-300 bg-blue-50/50'} rounded-r-md`}>
        <p className="font-medium">{help.definition}</p>
        <p className={isDark ? 'text-slate-400' : 'text-slate-600'}><strong className={isDark ? 'text-slate-300' : 'text-slate-700'}>Comment le lire ?</strong> {help.reading}</p>
      </div>
    </details>
  )
}
